import { DatePicker, Select, Table, TableColumnsType, Tooltip } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getPaymentRefunds } from '../../api/niceRefunds.api';
import Button from '../../components/Button';
import DownloadButton from '../../components/DownloadButton';
import ErrorResponse from '../../components/ErrorResponse';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import DatePickerTable from '../../components/statistics/DatePickerTable';
import SelectCampus from '../../components/SelectCampus';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import exportTable from '../../lib/exportTable';
import { getCommonCodeLabel } from '../../lib/store';
import { IGetPaymentRefunds, IPaymentRefund } from '../../models/niceRefunds.model';
import { RootState } from '../../store/rootReducer';
import { DateTableType } from '../../constans';
import TableEmpty from '../../components/common/TableEmpty';
import NiceRefundDetailModal from '../../components/payment/refund/modals/NiceRefundDetailModal';

const NicePaymentRefund = () => {
  const { t } = useTranslation();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetPaymentRefunds>();

  const [target, setTarget] = useState<string | undefined>();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [data, setData] = useState<any>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [refundDetailVisible, setRefundDetailVisible] = useState(false);
  // 선택한 환불내역 PK
  const [refundNo, setRefundNo] = useState<number>();
  const [defSelType, setDefSelType] = useState<number>(2);

  const [searchOption, setSearchOption] = useState<'DETAIL' | 'STUDENT_NAME' | 'APPROVED_NUM'>('DETAIL');
  const [processMtd, setProcessMtd] = useState<string>();
  const [searchInput, setSearchInput] = useState<string>();

  useEffect(() => {
    if (serverDate) {
      const date = moment(serverDate).format('YYYYMMDD');
      setStartDate(date);
      setEndDate(date);
    }
  }, [serverDate]);

  useEffect(() => {
    if (res && !isLoading) {
      let total = 0;
      res.data.forEach((item: IPaymentRefund) => {
        total += item.refundAmount;
      });
      setTotalAmount(total);
      setData(res.data);
    }
  }, [res, isLoading]);

  const onSearch = () => {
    if (!startDate || !endDate) {
      return infoNotification(t('payment.charge.message1'));
    }

    execute(getPaymentRefunds({
      campusCode: currentCampus,
      paymentTargetCode: target,
      srtDate: startDate,
      endDate,
      processMtd,
      memberName: searchOption === 'STUDENT_NAME' ? searchInput : undefined,
      approvalNumber: searchOption === 'APPROVED_NUM' ? searchInput : undefined,
    }));
  };

  const onExportTable = () => {
    const _data = _.cloneDeep(data);
    _data.forEach((item: any) => {
      item.monthNameCode = getCommonCodeLabel('SY0009', item.monthNameCode?.trim() || '', 'ko');
      item.paymentTargetCode = getCommonCodeLabel('PA0001', item.paymentTargetCode, 'ko');
      item.calculateReasonCode = getCommonCodeLabel('PA0008', item.calculateReasonCode, 'ko');
      item.refundDttm = item.refundDttm ? moment(item.refundDttm).format('YYYY.MM.DD HH:mm') : '';
      item.paymentChannelCode = getCommonCodeLabel('PA0004', item.paymentChannelCode, 'ko');
      item.processMtd = getCommonCodeLabel('PA0018', item.processMtd, 'ko');
      item.reasonCode = item.reasonCode ? getCommonCodeLabel('PA0016', item.reasonCode, 'ko') : '-';
      item.paymentAmount = item.paymentAmount;
      item.refundAmount = item.refundAmount;
      item.paymentMtdCode = getCommonCodeLabel('PA0002', item.paymentMtdCode, 'ko');
    });
    const currentDate = moment(new Date());
    exportTable(
      exportColumns,
      _data,
      `${t('payment.refund.1')}_(${currentDate.format('YYYYMMDD')})`,
      t('payment.refund.1'),
      [15, 10, 20, 15, 10, 20, 15, 15, 10, 20, 15, 15, 12, 12, 12, 18],
    );
  };

  const columns: TableColumnsType<IPaymentRefund> = [
    {
      title: '#',
      dataIndex: 'paymentRecordCode',
      key: 'paymentRecordCode',
      align: 'center',
    },
    {
      title: t('common.column.semester'),
      dataIndex: 'semesterName',
      key: 'semesterName',
      align: 'center',
    },
    {
      title: t('payment.charge.column1'),
      dataIndex: 'monthNameCode',
      key: 'monthNameCode',
      align: 'center',
      render: (value: string) =>
        getCommonCodeLabel('SY0009', value?.trim() || '', 'ko'),
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
    },
    {
      title: t('payment.refund.column1'),
      dataIndex: 'paymentTargetCode',
      key: 'paymentTargetCode',
      align: 'center',
      render: (value: string) => getCommonCodeLabel('PA0001', value, 'ko'),
    },
    {
      title: t('처리방식'),
      dataIndex: 'processMtd',
      key: 'processMtd',
      align: 'center',
      render: (value, record) => (
        <Tooltip title={getCommonCodeLabel('PA0016', record.reasonCode, 'ko')}>
          <span>{getCommonCodeLabel('PA0018', value, 'ko')}</span>
        </Tooltip>
      ),
    },
    {
      title: t('payment.coupon.placeholder2'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
      render: (value, record) => (
        <span
          className="pointer"
          onClick={() => {
            setRefundNo(record.refundNo);
            setRefundDetailVisible(true);
          }}
        >
          <u>{value}</u>
        </span>
      ),
    },
    {
      title: t('payment.charge.column4'),
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
    },
    {
      title: t('결제 승인 번호'),
      dataIndex: 'approvalNumber',
      align: 'center',
      render: value => value?.toLocaleString(),
    },
    {
      title: t('statistics.eventRefund.7'),
      dataIndex: 'paymentChannelCode',
      key: 'paymentChannelCode',
      align: 'center',
      render: value => getCommonCodeLabel('PA0004', value, 'ko'),
    },
    {
      title: t('student.student.column42'),
      dataIndex: 'paymentMtdCode',
      key: 'paymentMtdCode',
      align: 'center',
      render: (value) => getCommonCodeLabel('PA0002', value, 'ko'),
    },
    {
      title: t('payment.record.column3'),
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      align: 'center',
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: t('payment.refund.column2'),
      dataIndex: 'refundAmount',
      key: 'refundAmount',
      align: 'center',
      render: (value: number) => value?.toLocaleString(),
    },
    {
      title: t('payment.refund.column3'),
      dataIndex: 'calculateReasonCode',
      key: 'calculateReasonCode',
      align: 'center',
      render: (value: string) => getCommonCodeLabel('PA0008', value, 'ko'),
    },
    {
      title: t('payment.refund.column4'),
      dataIndex: 'refundDttm',
      key: 'refundDttm',
      align: 'center',
      render: (value: string) => moment(value).format('YYYY.MM.DD HH:mm'),
    },
  ];

  /** 화면 테이블과 동일 + 엑셀에만 reasonCode(PA0016 라벨) 컬럼 추가 */
  const exportColumns: TableColumnsType<IPaymentRefund> = [
    ...columns.slice(0, 6),
    {
      title: t('payment.refund.reasonCode', '관리자 환불 사유'),
      dataIndex: 'reasonCode',
      key: 'reasonCode',
      align: 'center',
    },
    ...columns.slice(6),
  ];

  console.log(res);

  return (
    <>
      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label>
              <div>
                <SelectCampus />
              </div>
            </li>
            <li>
              <label>{t('payment.charge.column3')}</label>
              <div>
                <Select
                  placeholder={t('payment.charge.column3')}
                  value={target}
                  onChange={(value) => setTarget(value)}
                >
                  <Select.Option value="">{t('common.text.all')}</Select.Option>
                  <Select.Option value="PT">{t('payment.charge.option1')}</Select.Option>
                  <Select.Option value="TU">{t('payment.charge.option2')}</Select.Option>
                  <Select.Option value="ON">{t('payment.charge.option3')}</Select.Option>
                  <Select.Option value="AL">{t('payment.charge.option4')}</Select.Option>
                  <Select.Option value="ST">{t('payment.charge.option5')}</Select.Option>
                  <Select.Option value="SC">{t('payment.charge.option6')}</Select.Option>
                  <Select.Option value="ET">기타</Select.Option>
                </Select>
              </div>
            </li>
            <li>
              <label>{t('처리방식')}</label>

              <div>
                <Select
                  className="w-200"
                  placeholder="처리방식"
                  value={processMtd}
                  onChange={value => setProcessMtd(value)}
                >
                  {['01', '02'].map((item) => (
                    <Select.Option value={item}>
                      {getCommonCodeLabel('PA0018', item, 'ko')}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </li>
            <li>
              <label>{t('검색')}</label>
              <div className="flex">
                <Select
                  defaultValue={'DETAIL'}
                  value={searchOption}
                  onChange={(value: 'DETAIL' | 'STUDENT_NAME' | 'APPROVED_NUM') => setSearchOption(value)}
                >
                  <Select.Option value="DETAIL">
                    {t('상세 검색')}
                  </Select.Option>

                  <Select.Option value="STUDENT_NAME">
                    {t('학생명')}
                  </Select.Option>

                  <Select.Option value="APPROVED_NUM">
                    {t('결제승인번호')}
                  </Select.Option>
                </Select>

                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') onSearch();
                  }}
                  className="form-control input-text ml-2"
                  disabled={searchOption === 'DETAIL'}
                />
              </div>
            </li>
            <li className="period">
              <label>{t('sms.list.column1')}</label>
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center">
                  <div className="date-picker__area">
                    <DatePicker
                      value={startDate ? moment(startDate) : undefined}
                      onChange={(e) => {
                        if (e === null) setStartDate('');
                        else setStartDate(moment(e).format('YYYYMMDD'));
                      }}
                    />
                    ~
                    <DatePicker
                      value={endDate ? moment(endDate) : undefined}
                      onChange={(e) => {
                        if (e === null) setEndDate('');
                        else setEndDate(moment(e).format('YYYYMMDD'));
                      }}
                    />
                  </div>
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small black"
                      onClick={onSearch}
                      text={t('common.button.search')}
                    />
                  </div>
                </div>
                <div className="comm-btn__area  mt-2">
                  <DatePickerTable
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    type={DateTableType.ALL}
                    defSelType={defSelType}
                    setDefSelType={setDefSelType}
                  />
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className="content scrollbar">
        {isLoading && <Loader />}
        <ErrorResponse error={error} />
        <SimpleHeader
          iconFile="icon-payment-big.svg"
          parentMenu={t('common.menu.payment')}
          currentMenu={t('payment.refund.title1')}
          onlyTitle={true}
        />

        <div className="col-frame">
          {data.length > 0 ? (
            <>
              <div className="comm-bx__between">
                <div className="comm-sch_result">
                  {t('payment.refund.text1')}: <b>{totalAmount.toLocaleString()}</b>{t('payment.charge.text2')}
                </div>
              </div>
              <div className="comm-table__area">
                <Table
                  columns={columns}
                  dataSource={data}
                  pagination={{
                    pageSize: 9999999,
                  }}
                />
              </div>
              <div className="comm-bx__between">
                <div className="comm-btn__area">
                  <DownloadButton onClick={onExportTable} />
                </div>
              </div>
            </>
          ) : (
            <TableEmpty text={t('payment.refund.text2')} />
          )}
        </div>

        {refundDetailVisible &&
          <NiceRefundDetailModal
            visible={refundDetailVisible}
            setVisible={setRefundDetailVisible}
            refundNo={refundNo}
            paymentChannelCode={res?.data.find(it => it.refundNo === refundNo)?.paymentChannelCode}
          />
        }
      </div>
    </>
  );
};

export default NicePaymentRefund;
